<template>
  <div
      style="overflow: hidden; min-height: calc(100vh - 6rem); padding-bottom: 3rem;"
      :class="{'view-checkout-page--for-tourist': isTouristFlow}">
    <base-full-loader ref="fullLoader"/>
    <template v-if="cartAvailable || showConfirmation">
      <checkout-progress v-if="!!step" :step="step"/>
      <transition name="fade" mode="out-in">
        <component :is="selectedView || 'placeholder-component'"
                   :cart-data="cartData"
                   :order-data="orderData"
                   :order-config="orderConfig"
                   :key="componentKey"
                   :segment="$props.segment"
                   :show-text="showText"
                   :show-buttons="showButtons"
                   :step="step"
                   :user-type="userType"
                   @order-status="orderStatus = $event"
                   @blacklist="isBlacklisted"
                   @show-nav="showNav = $event"
                   @go-to-identification="toIdentify"
                   @go-back="goStepBack"
                   @child-loading="updateChildLoadingStatus"
                   @update-component="setCurrentComponent"/>
      </transition>
    </template>
    <empty-cart v-else-if="!isLoading"/>
    <checkout-navigation-buttons v-if="navigationButtonsVisible && !isLoading && showNav"
                                 :fixed="selectedView === 'the-checkout-preview-cart'"
                                 @back="goStepBack"
                                 @continue="goStepForward"/>
    <flash-message :position="'right bottom'"></flash-message>
  </div>
</template>

<script>
import { getCookie, setCookie } from '@/utils/cookieController'
import { fetchCart, addTariffToCart } from '@/api/cart'
import { checkoutViews } from '@/components/checkout/config/checkoutViews'
import { success, fail, paymentFail } from '@/components/checkout/config/orderConfirmationProps'
import { customEvent } from '@/services/customEventBus'
import { getUrlParam, getTouristFlowDataFromUrl } from '@/utils/urlMethods'
import { lsGet, lsRemove } from '@/utils/localStorageController'
import CheckoutLayout from '@/components/checkout/checkout-layout'
import CheckoutProgress from '@/components/checkout/checkout-progress'
import PlaceholderComponent from '@/components/common/placeholder-component'
import TheCheckoutPreviewCart from '@/components/checkout/cart-preview/the-checkout-preview-cart'
import CheckoutPayment from '@/components/checkout/checkout-payment/checkout-payment'
import userIdentification from '@/components/checkout/user-identification/user-identification'
import userIdentificationExisting from '@/components/checkout/user-identification/user-identification-existing'
import userIdentificationFreeNumber from '@/components/checkout/user-identification/user-identification-free-number'
import userIdentificationNewCable from '@/components/checkout/user-identification/user-identification-new-cable'
import OrderConfirmation from '@/components/checkout/order-confirmation/order-confirmation'
import EmptyCart from '@/components/checkout/empty-cart'
import CheckoutNavigationButtons from '../../../components/checkout/checkout-navigation-buttons'
import { CART_EXPIRATION_DAYS, defaultLanguage, localStorageKeys, prepaidTourist } from '@/config/constants'

export default {
  name: 'view-checkout-page',
  components: {
    CheckoutNavigationButtons,
    TheCheckoutPreviewCart,
    CheckoutPayment,
    PlaceholderComponent,
    CheckoutLayout,
    CheckoutProgress,
    userIdentification,
    userIdentificationExisting,
    userIdentificationFreeNumber,
    userIdentificationNewCable,
    OrderConfirmation,
    EmptyCart
  },
  props: {
    segment: {
      type: String,
      default: 'PRIVATNI'
    },
    lang: String
  },
  data () {
    return {
      cartData: {},
      orderData: {},
      step: 1,
      componentKey: new Date().getTime(),
      selectedView: '',
      orderStatus: '',
      showConfirmation: false,
      childLoading: false,
      showText: true,
      showButtons: true,
      isLoading: true,
      userType: '',
      showNav: true
    }
  },
  computed: {
    views () {
      return { ...checkoutViews }
    },
    successOrder () {
      return { ...success(this) }
    },
    failOrder () {
      return { ...fail(this) }
    },
    paymentFail () {
      return { ...paymentFail(this) }
    },
    navigationButtonsVisible () {
      if (this.selectedView) {
        const currentView = Object.values(this.views).find(view => view.component === this.selectedView)
        return currentView.navigationVisible && !this.childLoading
      }
      return false
    },
    orderConfig () {
      if (this.orderStatus === 'success') {
        return this.successOrder
      } else if (this.orderStatus === 'failed') {
        return this.failOrder
      } else if (this.orderStatus === 'payment-failed' || this.orderStatus === 'neuspjesno-placanje') {
        return this.paymentFail
      }
      return {}
    },
    cartAvailable () {
      return this.cartData?.entries?.length
    },
    isTouristFlow () {
      return !!getTouristFlowDataFromUrl()
    },
    isFromQrCode () {
      return prepaidTourist.isFromQrCode
    }
  },
  methods: {
    fetchCartData (cartId) {
      if (cartId) {
        this.$loader.show()
        fetchCart(cartId).then(({ data }) => {
          this.cartData = data || {}
        }).finally(() => {
          this.$loader.hide()
          this.isLoading = false
        })
      }
    },
    setCurrentComponent (name, userType) {
      window.scroll({
        top: 0,
        left: 0,
        behavior: 'smooth'
      })
      if (userType) {
        this.userType = userType === 'new' ? 'Novi' : 'Postojeci'
      }
      if (!name) {
        const defaultComponent = this.views[Object.keys(this.views)[0]]
        this.selectedView = defaultComponent.component
        this.step = defaultComponent.step
      } else {
        const viewComponent = Object.values(this.views).find(view => view.name === name)
        if (viewComponent) {
          this.selectedView = viewComponent.component
          this.step = viewComponent.step
        } else {
          this.selectedView = this.views[Object.keys(this.views)[0]].component // provide other fallback, error screen or smth
          this.step = this.views[Object.keys(this.views)[0]].step
        }
      }
    },
    /**
     * gets new unique string so we can have nice transition
     */
    getNewComponentKey () {
      this.componentKey = new Date().getTime()
    },
    goStepBack () {
      const currentView = Object.values(this.views).find(view => view.component === this.selectedView)
      const toPrevPage = lsGet('paymentType') // if paymentType is available redirect to previous page because identification is skipped
      const showNewUserComponent = lsGet('showNewUserComponent')
      const tariffPage = lsGet(localStorageKeys.touristTariffListPage)
      if (this.isTouristFlow && !prepaidTourist.isFromQrCode) {
        location.href = tariffPage || this.URL.private.forTouristEN
      } else if (this.isTouristFlow && prepaidTourist.isFromQrCode) {
        location.href = tariffPage + '?' + prepaidTourist.queryParamNames.qr + '=' + prepaidTourist.queryParamValues.touristSimResources
      } else if (toPrevPage) {
        const isCurrentUrl = document.referrer.split('/')[3] !== 'kupovina'
        const isReferrerEmpty = document.referrer !== ''
        const url = isCurrentUrl && isReferrerEmpty ? document.referrer : this.URL.private.PLP.mobile
        window.location.replace(url)
      } else if (showNewUserComponent && currentView && currentView.previous !== 'cart-preview') { // case when buying only tarrif
        this.setCurrentComponent('identification-new-cable')
      } else if (currentView && currentView.previous) {
        this.setCurrentComponent(currentView.previous)
        this.getNewComponentKey()
      }
    },
    goStepForward (isValid) {
      const currentView = Object.values(this.views).find(view => view.component === this.selectedView)
      const valid = isValid || currentView.valid
      if (currentView && currentView.next && valid && !this.isTouristFlow) {
        this.setCurrentComponent(currentView.next)
        this.getNewComponentKey()
      } else if (this.isTouristFlow) {
        customEvent.$emit('validate-tourist-step')
      } else {
        customEvent.$emit('validate-step')
      }
    },
    viewStatus (status) {
      if (status) {
        this.showConfirmation = lsGet('ec_show_order_confirmation')
        this.orderStatus = status
        lsRemove('ec_show_order_confirmation')
        return 'order-confirmation'
      }
      lsRemove('ec_show_order_confirmation')
      return ''
    },
    updateChildLoadingStatus (loadingState = false) {
      this.childLoading = loadingState
    },
    isBlacklisted () {
      this.orderStatus = 'failed'
      this.showText = false
      this.showButtons = false
      this.setCurrentComponent('order-confirmation')
    },
    toIdentify () {
      this.setCurrentComponent('identification-existing')
      this.showNav = true
    },
    setViewComponent () {
      const view = this.viewStatus(getUrlParam('status'))
      const paymentType = lsGet('paymentType')
      const showNewUserComponent = lsGet('showNewUserComponent')
      const urlParams = getTouristFlowDataFromUrl()
      const hasTariffCode = urlParams?.tariffCode && urlParams?.lang
      if (!hasTariffCode) {
        // skip if tariffCode in url because addPrepaidTariff call it again
        const guid = getCookie('guid')
        this.fetchCartData(guid)
      }

      if (paymentType) {
        this.setCurrentComponent('cart-preview')
      } else if (showNewUserComponent) {
        this.setCurrentComponent('identification-new-cable')
      } else if (hasTariffCode) {
        this.$i18n.locale = urlParams?.lang || defaultLanguage
        this.setCurrentComponent('cart-preview')
        this.addPrepaidTariff(urlParams)
      } else {
        this.setCurrentComponent(view)
      }
    },
    addPrepaidTariff (params) {
      const dataToSend = {
        tariffCode: params.tariffCode,
        lang: params.lang,
        ...(params?.dealerID && { shopId: params?.dealerID }),
        ...(params?.productType && { productType: params.productType })
      }
      if (params.isSimResources) {
        dataToSend.isSimResources = true
      }
      addTariffToCart(dataToSend, true)
        .then(({ data }) => {
          setCookie('guid', data.guid, CART_EXPIRATION_DAYS)
          this.fetchCartData(data.guid)
        })
    }
  },
  created () {
    this.setViewComponent()
    customEvent.$on('validation-done', (payload, order, data) => {
      this.goStepForward(payload)
      this.orderStatus = order
      this.orderData = data || {}
    })

    this.flashMessage.setStrategy('multiple')
  },
  mounted () {
    if (this.lang) this.$i18n.locale = this.lang || defaultLanguage
    this.$loader.show()
    setTimeout(() => {
      if (!this.cartAvailable) {
        this.$loader.hide() // if users lands on this page directly without cart hide loader after 2 sec
        this.isLoading = false
      }
    }, 2000)
  },
  beforeDestroy () {
    customEvent.$off('validation-done')
  }
}
</script>

<style lang="scss" scoped>
.view-checkout-page--for-tourist {
  min-height: calc(100vh - 10rem)!important;
  padding-bottom: 0!important;
}
</style>
